import { historyApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { ServiceResponseI, DataI } from "../../interfaces";
import { HistoryI, HistorySummaryI, GetHistoryParamsI } from "@interfaces";
import { AxiosError } from "axios";

const getHistory = async ({
  pagination,
  sorting,
  branchId,
  productId,
  typeId,
  dateFrom,
  dateTo,
  globalFilter,
}: GetHistoryParamsI): Promise<ServiceResponseI<DataI<HistoryI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (globalFilter) params.append("search", globalFilter);
    if (branchId) params.append("branchId", branchId.toString());
    if (productId) params.append("productId", productId.toString());
    if (typeId) params.append("typeId", typeId.toString());
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const { data } = await historyApi.get<ServiceResponseI<DataI<HistoryI>>>(
      "/movements",
      { params, headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

const getHistorySummary = async (
  params: Omit<GetHistoryParamsI, "pagination" | "sorting">,
): Promise<ServiceResponseI<HistorySummaryI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const p = new URLSearchParams();
    if (params.branchId) p.append("branchId", params.branchId.toString());
    if (params.dateFrom) p.append("dateFrom", params.dateFrom);
    if (params.dateTo) p.append("dateTo", params.dateTo);

    const { data } = await historyApi.get<ServiceResponseI<HistorySummaryI>>(
      "/movements/summary",
      { params: p, headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useHistory = (queryParams: GetHistoryParamsI) => {
  const historyQuery = useQuery(
    ["history", queryParams],
    () => getHistory(queryParams),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { historyQuery };
};

export const useHistorySummary = (
  params: Omit<GetHistoryParamsI, "pagination" | "sorting">,
) => {
  const summaryQuery = useQuery(
    ["history-summary", params],
    () => getHistorySummary(params),
    { refetchOnWindowFocus: false },
  );
  return { summaryQuery };
};
